import { useEffect } from "react";
import { track } from "../shared/analytics/posthog";

/**
 * Google Ads "WhatsApp Contact" conversion.
 *
 * Most real villa/activity leads reach us on WhatsApp (reserve → confirm on
 * WhatsApp → 40% deposit), never through the on-site booking thank-you page.
 * Those leads are invisible to Google Ads, so Smart Bidding optimises blind
 * and the account reports 0 conversions. This listener fires a conversion the
 * moment a visitor clicks any WhatsApp link, so ad-attributed WhatsApp leads
 * are finally counted and the bidding can optimise toward them.
 *
 * The gtag loader itself lives in the root document
 * (`apps/majourneys/src/routes/__root.tsx`). This is a single delegated,
 * capture-phase click listener so every WhatsApp anchor across the app (villa
 * pages, need-help card, footer, account, booking bar, …) is covered without
 * touching each component. Google only records a conversion when the click is
 * attributable to an ad, so firing site-wide is safe.
 */

// AW-969299995 → "WhatsApp Contact" conversion action (Contact category,
// count One, primary). Sibling of the "Reservation" purchase conversion.
const WHATSAPP_CONVERSION_SEND_TO = "AW-969299995/W4NmCNfXj9IcEJuwmc4D";

const WHATSAPP_LINK_SELECTOR =
  'a[href*="wa.me"], a[href*="api.whatsapp.com"], a[href*="web.whatsapp.com"], a[href^="whatsapp:"]';

function isConfigured(): boolean {
  return !WHATSAPP_CONVERSION_SEND_TO.includes("<LABEL>");
}

export function WhatsAppConversionTracker() {
  useEffect(() => {
    if (!isConfigured()) return;

    function onClick(event: MouseEvent) {
      const target = event.target as Element | null;
      const link = target?.closest?.(WHATSAPP_LINK_SELECTOR);
      if (!link) return;
      const w = window as Window & {
        gtag?: (...args: unknown[]) => void;
        ttq?: { track?: (...args: unknown[]) => void };
      };
      w.gtag?.("event", "conversion", { send_to: WHATSAPP_CONVERSION_SEND_TO });
      // TikTok "Contact" — the WhatsApp equivalent of the website booking
      // conversion. Without it a TikTok campaign sending traffic to the site
      // sees only visitors who complete the on-site booking flow, and scores
      // every lead that peels off to WhatsApp (still most of them) as a miss.
      w.ttq?.track?.("Contact", {}, { event_id: `wa-${Date.now()}` });
      // Funnel exit: most leads leave the site here rather than at checkout.
      track("whatsapp_clicked", { path: window.location.pathname });
    }

    // Capture phase so the hit fires even if a handler calls stopPropagation.
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
